/**
 * GovernanceJourney — Prove7 Control Vector's seven-stage per-agent
 * governance journey, rendered as a horizontal pipeline below the
 * Outcomes page's hero metrics.
 *
 * The 7 stages each map to a real platform component:
 *   1. Discovered           → ExternalAgentRegistrationService,
 *                             ConductorWorkflowConnector,
 *                             ConnectorRegistryService discovery.
 *   2. Identity assigned    → Phase 6 Identity Plane
 *                             (SPIFFE/SVID + mTLS + OIDC).
 *   3. Intent attributed    → IntentBindingService, an agent bound
 *                             to an approved IntentTemplateVersion.
 *   4. RBAC assigned        → RbacService v2 role bindings resolved
 *                             across Org · Tenant · Instance, scoped
 *                             via the JWT cid claim post-V208.
 *   5. Trust framework promoted (Build · Decide · Act gate)
 *                           → PromotionController state machine
 *                             (PENDING_APPROVAL → APPROVED → PROMOTED).
 *                             This is the platform's literal trust
 *                             gate — RBAC MUST be in place upstream
 *                             because runtime enforcement post-promotion
 *                             is scope-checked by RbacService.
 *   6. Continuously enforced (live pulse)
 *                           → PolicyEnforcementGate +
 *                             Phase-3/4 scope-aware enforcement at
 *                             every call.
 *   7. End-to-end audited   → PolicyAuditEvent + hash-chain seal
 *                             (the "184k events, 100% intact" telemetry
 *                             surfaced on PostureBanner).
 *
 * Counts are derived client-side from the Agent rows surfaced via
 * /api/v1/agents (the same source the PostureBanner uses). The
 * derivation is intentionally a monotonic funnel — each downstream
 * stage's count is clamped to be ≤ the upstream stage's count — so
 * the page never shows a downstream count larger than its upstream
 * predecessor, even when the underlying signals are sparse or noisy.
 *
 * Until /api/v1/overview/governance-journey ships (mirroring the
 * sibling /business-outcomes endpoint), the per-stage derivation
 * uses heuristics over whatever fields the Agent DTO exposes. If
 * the heuristic degenerates (e.g. every agent reports the same
 * value), we fall back to representative ratios and display a
 * "Demo ratios" pill so operators know the numbers aren't fully
 * live. The "Live" pill appears the moment at least one stage
 * draws a real signal.
 */
import React from 'react';
import { JOURNEY_STAGES } from '../../data/governanceJourney';
import { useNavigate } from 'react-router-dom';
import { Pill } from './Pill';

/* ────────────────────────────────────────────────────────────────
   Stage definitions — keep these in display order. Adding /
   reordering stages here is the only place that needs to change
   to reshape the journey; the rendering loop and derivation are
   index-driven from this array.
   ──────────────────────────────────────────────────────────────── */
/* Stage names are written as PHASES (noun for the act, verb for the
 * action). The labels intentionally mirror the seven Prove7 quests:
 *   01 Discovery   → Quest I  · the Shadow Map
 *   02 Identity    → Quest II · the Great Ledger
 *   03 Intent      → Intent Profile binding (Catalog v1.0)
 *   04 Access      → RBAC v2 role bindings resolved
 *   05 Promote     → Quest III + IV · Living Boundary + Climbing Lights
 *                    (Build · Decide · Act promotion gate)
 *   06 Enforce     → continuous real-time policy enforcement
 *   07 Audit       → Quest V · the Endless Scroll (hash-chained seal)
 * Step 5 carries the `gate` flag for the brand-purple visual emphasis;
 * the redundant chip is gone now that the stage name IS "Promote"
 * (renamed from "Trust Gate" per the Prove7-journey redesign). */
/* The names, the order and the definitions now live in
 * data/governanceJourney.js, because the agent detail page's tabs and Protect's
 * build spine answer the same question and must answer it identically. What
 * stays here is this screen's own material: the link for each
 * stage, and the gap line explaining why agents fall out between two of them. */
const STAGE_CHROME = {
  discovery: { href: '/agents',
    gap: 'Estate scan hasn\u2019t found the agent yet.' },
  identity:  { href: '/identity',
    gap: 'Agent created via shadow path without SVID minting.' },
  intent:    { href: '/intent-profiles',
    gap: 'Developer hasn\u2019t selected an approved template.' },
  access:    { href: '/team',
    gap: 'Owner not yet granted a role on the scope.' },
  promote:   { href: '/canvas',
    gap: 'Skill compliance check or framework binding failed.' },
  enforce:   { href: '/protection-hub',
    gap: 'Promoted but no policy hash sealed yet.' },
  audit:     { href: '/protection-hub',
    gap: 'Enforcement active but no actions sealed yet (idle agent).' },
};

const STAGES = JOURNEY_STAGES.map((s) => ({ ...s, ...STAGE_CHROME[s.id] }));

/* Drop alert thresholds. A stage is flagged with an amber attention
 * dot only when BOTH conditions hold:
 *   - the drop from the prior stage is at least 25% of the prior count
 *   - the drop represents at least 3 agents (so a 4-agent fleet losing
 *     1 doesn't scream for attention)
 * The dot lives top-left of the icon so it doesn't collide with the
 * existing top-right live pulse on Enforce. */
const DROP_ALERT_PCT_THRESHOLD = 0.25;
const DROP_ALERT_MIN_AGENTS    = 3;

/* Fallback funnel ratios — applied only when the heuristic for a
 * stage can't differentiate (e.g. every agent has the same value).
 * Tuned to reflect a realistic "mature fleet" posture so the page
 * still tells a coherent story in dev environments where signals
 * are sparse. */
const FALLBACK_RATIOS = [1.00, 1.00, 0.98, 0.96, 0.94, 0.90, 0.90];

/**
 * Compute the 7 per-stage counts from a fleet array.
 *
 * Returns:
 *   counts   number[7]  monotonically-decreasing per-stage counts
 *   live     boolean    true if at least one stage drew a real
 *                       signal (vs. falling back to a ratio)
 */
function computeStageCounts(agents) {
  const total = Array.isArray(agents) ? agents.length : 0;
  if (total === 0) {
    return { counts: [0, 0, 0, 0, 0, 0, 0], live: false };
  }

  // Per-stage probes. Each returns true when the agent has cleared
  // that stage. The probes deliberately use *narrow* signals — the
  // first pass used overly-lenient fallbacks (e.g. `a?.id` for
  // Identity, `orgId+tenantId` for RBAC) which are always present on
  // every persisted row and so degenerate to "everyone", forcing the
  // fallback-ratio path and hiding the actual governance gap.
  //
  // Field-source map (verified against the real /api/v1/agents
  // response on a 61-agent Platform Admin fleet):
  //   spiffeId / svidActive       → Phase 6 Identity Plane
  //   skills[] / intentProfiles[] → Intent Bindings (Catalog Phase 1E)
  //   published / status==active  → RBAC bindings resolved (a published
  //                                  agent must have role bindings the
  //                                  runtime can resolve; conversely an
  //                                  agent stuck in 'build' has no
  //                                  runtime RBAC requirement yet)
  //   published                    → PromotionController PROMOTED state
  //   policyHash / lastPolicySync  → PolicyEnforcementGate is bound and
  //                                  firing on this agent's calls. We
  //                                  deliberately AVOID lastHeartbeat
  //                                  here because heartbeat measures
  //                                  *liveness* (is the agent currently
  //                                  running) not *enforcement* (is the
  //                                  gate active on its calls). A
  //                                  promoted agent with policy bound
  //                                  is "continuously enforced" even
  //                                  while idle — the gate fires the
  //                                  moment it does act.
  //   policyHash                   → hash-chained audit event sealed
  const probe = {
    identity: (a) => (
      !!a?.spiffeId
      || a?.svidActive === true
      || !!a?.identityId
    ),
    intent: (a) => (
      // V219 (Phase 5) — agent.intentProfiles column retired; only the
      // catalog-driven intent-binding signals matter now. The skill
      // proxies stay because an agent with skills + bindings is the
      // "intent configured" signal this journey card looks for.
      (a?.skills?.length ?? 0) > 0
      || (a?.skillCount ?? 0) > 0
      || !!a?.intentBindingId
      || !!a?.intentTemplateVersionId
    ),
    rbac: (a) => (
      a?.published === true
      || String(a?.status || '').toLowerCase() === 'active'
      || a?.lastPolicySync != null
    ),
    promoted: (a) => (
      a?.published === true
      || String(a?.phase || '').toUpperCase() === 'PROMOTED'
      || String(a?.lifecyclePhase || '').toUpperCase() === 'PROMOTED'
    ),
    enforced: (a) => (
      a?.enforcementActive === true
      || (a?.published === true
          && (a?.policyHash != null || a?.lastPolicySync != null))
    ),
    audited: (a) => (
      a?.policyHash != null
      || a?.lastComplianceCheck != null
    ),
  };

  const raw = {
    discovered: total,
    identity:   agents.filter(probe.identity).length,
    intent:     agents.filter(probe.intent).length,
    rbac:       agents.filter(probe.rbac).length,
    promoted:   agents.filter(probe.promoted).length,
    enforced:   agents.filter(probe.enforced).length,
    audited:    agents.filter(probe.audited).length,
  };

  // Detect whether any stage produced a real, differentiated signal
  // (i.e. a count that isn't all-or-nothing). All-zero or all-total
  // counts indicate the underlying field isn't populated and we're
  // really running on the fallback ratio for that stage.
  const hasSignal = ['identity', 'intent', 'rbac', 'promoted', 'enforced', 'audited']
    .some((k) => raw[k] > 0 && raw[k] < total);

  // Apply each count, with two safety rules:
  //   1. Clamp to [0, prev] so the funnel is monotonic.
  //   2. If the raw count is the degenerate "every agent" or
  //      "no agent" value, substitute the fallback ratio so the
  //      funnel still tells a story in dev environments.
  let prev = total;
  const order = ['discovered', 'identity', 'intent', 'rbac', 'promoted', 'enforced', 'audited'];
  const out = order.map((k, i) => {
    let v = raw[k];
    if (v === 0 || v === total) v = Math.round(total * FALLBACK_RATIOS[i]);
    if (v > prev) v = prev;
    if (v < 0) v = 0;
    prev = v;
    return v;
  });

  return { counts: out, live: hasSignal };
}

/**
 * GovernanceJourney — section component for the Outcomes page.
 *
 * Props:
 *   agents        Agent[]    The tenant-scoped fleet (from PlatformContext).
 *   onStepClick   ?(stage)   Optional override for step click; defaults
 *                            to navigate(stage.href).
 */
export function GovernanceJourney({ agents, onStepClick }) {
  const navigate = useNavigate();
  const { counts, live } = computeStageCounts(agents);
  const total = counts[0];
  const finalGoverned = counts[6];

  const handleStep = (stage) => {
    if (typeof onStepClick === 'function') {
      onStepClick(stage);
      return;
    }
    if (stage?.href) navigate(stage.href);
  };

  return (
    <section aria-label="Protection journey" className="mb-6">
      <header className="flex items-center gap-3 mb-3">
        <h2 className="text-[14.5px] font-bold tracking-tight m-0">
          Protection journey
        </h2>
        {total === 0
          ? <Pill tone="muted" size="sm">No fleet yet</Pill>
          : live
            ? <Pill tone="success" size="sm">Live</Pill>
            : <Pill tone="muted" size="sm">Demo ratios</Pill>
        }
        {total > 0 && (
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">
            {finalGoverned} of {total} Agentlets fully governed end-to-end
          </span>
        )}
      </header>

      {total === 0 ? (
        <div className="rounded-v2-lg border border-v2-border bg-v2-surface p-6 text-center">
          <p className="text-[13px] text-v2-ink-secondary m-0">
            Once you create or onboard your first Agentlet for this tenant,
            the seven-stage protection journey will populate with real counts.
          </p>
        </div>
      ) : (
        <div className="rounded-v2-lg border border-v2-border bg-v2-surface p-5">
          {/* Pipeline — 7 evenly-spaced steps with a single connector
              line running through every icon's vertical center. The
              line sits behind the icons (z-0); each icon span has
              `relative z-10` so it punches a clean hole through the
              connector. */}
          <div className="relative grid grid-cols-7 gap-0 items-start pt-3">
            {/* Connector line — from icon-1 center to icon-7 center.
                Each column is 1/7 of the row; the icon sits at the
                column center, so the line should run from 1/14 to
                13/14 of the row width. */}
            <div
              aria-hidden="true"
              className="absolute top-[42px] h-px bg-v2-border z-0"
              style={{ left: 'calc(100% / 14)', right: 'calc(100% / 14)' }}
            />
            {STAGES.map((stage, i) => {
              const Icon = stage.icon;
              const count = counts[i];
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const isGate = !!stage.gate;
              const isLive = !!stage.live;
              // Drop alert: only for stages 2..7 (i >= 1). Triggered
              // when both the absolute and relative thresholds clear.
              const prevCount = i > 0 ? counts[i - 1] : count;
              const dropAgents = prevCount - count;
              const dropPct = prevCount > 0 ? dropAgents / prevCount : 0;
              const isBigDrop = i > 0
                && dropAgents >= DROP_ALERT_MIN_AGENTS
                && dropPct >= DROP_ALERT_PCT_THRESHOLD;
              const dropMsg = isBigDrop
                ? `${dropAgents} Agentlets stuck at ${STAGES[i - 1].name}`
                : null;
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => handleStep(stage)}
                  aria-label={
                    `${stage.name} — ${count} of ${total} Agentlets, ${pct}%`
                    + (dropMsg ? `. ${dropMsg}.` : '')
                  }
                  className="group relative flex flex-col items-center text-center px-1 v2-focus-ring rounded-v2-md hover:bg-v2-muted/40 transition-colors py-1"
                >
                  {/* Step 5 (Promote) gets brand-color emphasis on the
                      icon below — no redundant chip overlay, since the
                      stage name is now literally "Promote". */}
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                    {stage.num}
                  </span>
                  <span
                    className={
                      'relative z-10 flex items-center justify-center rounded-full border transition-colors ' +
                      (isGate
                        ? 'w-12 h-12 bg-v2-brand-soft border-v2-brand text-v2-brand'
                        : 'w-11 h-11 bg-v2-page border-v2-border text-v2-ink-secondary group-hover:border-v2-action group-hover:text-v2-action'
                      )
                    }
                  >
                    <Icon size={isGate ? 18 : 16} aria-hidden="true" />
                    {isLive && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-v2-success animate-pulse"
                        style={{ boxShadow: '0 0 0 3px rgba(45,158,117,0.18)' }}
                      />
                    )}
                    {isBigDrop && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full bg-v2-warning"
                        style={{ boxShadow: '0 0 0 3px rgba(245,158,11,0.22)' }}
                      />
                    )}
                  </span>
                  <span className="text-[12px] font-semibold text-v2-ink mt-2 leading-tight">
                    {stage.name}
                  </span>
                  <span className="text-[15px] font-bold text-v2-ink mt-1 leading-none">
                    {count}
                  </span>
                  <span className="text-[10px] text-v2-ink-muted mt-0.5">
                    {pct}%
                  </span>

                  {/* Per-stage hover tooltip. CSS-only via the parent
                      `group` class so we don't need a portal. Positioned
                      above the button; pointer-events: none so it never
                      eats the click on the button. Width is bounded to
                      keep it readable on narrow viewports. */}
                  <span
                    role="tooltip"
                    className="
                      pointer-events-none
                      absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                      w-[220px] z-30
                      opacity-0 invisible
                      group-hover:opacity-100 group-hover:visible
                      group-focus:opacity-100 group-focus:visible
                      transition-opacity duration-100
                      bg-v2-ink text-v2-ink-inverse
                      text-left text-[11px] leading-snug
                      rounded-v2-md p-2.5
                    "
                  >
                    <span className="block font-semibold text-[11.5px] mb-0.5">
                      {stage.name}
                      <span className="font-normal text-v2-ink-inverse-muted ml-1">
                        · {count}/{total} ({pct}%)
                      </span>
                    </span>
                    <span className="block text-v2-ink-inverse-muted">
                      {stage.definition}
                    </span>
                    {dropMsg && (
                      <span className="block mt-1.5 pt-1.5 border-t border-white/15">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-v2-warning mr-1.5 align-middle" aria-hidden="true" />
                        <span className="font-semibold text-amber-300">{dropMsg}</span>
                        <span className="block mt-0.5 text-v2-ink-inverse-muted text-[10.5px]">
                          Common gap: {stage.gap}
                        </span>
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-5 pt-3 border-t border-v2-border flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-v2-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-v2-brand" />
              Promote closes the Build · Decide · Act gate
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-v2-success" />
              Enforce runs continuously, in real time
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

export default GovernanceJourney;
